import { memo } from "react";

import { useAdonisChat } from "../AdonisChatProvider/AdonisChatProvider";
import { Message } from "../streaming/streaming-message-types";

import { AssistantMessage } from "./messages/AssistantMessage";
import type { ChatMessageProps } from "./messages/ChatMessage";
import { UserMessage } from "./messages/UserMessage";

import "./messages.css";

export interface MessageListProps {
  messages: Message[];
  disableAnimations?: boolean;
  toolStatusRenderer?: ChatMessageProps["toolStatusRenderer"];
}

// Helper to check if a message is tool-result-only
const isToolResultOnlyUser = (m: Message) =>
  m.role === "user" &&
  Array.isArray(m.content) &&
  m.content.length > 0 &&
  m.content.every(
    (c: {
      type: "text" | "tool_use" | "tool_result";
      tool_use_id?: string;
      id?: string;
      name?: string;
      input?: Record<string, unknown>;
    }) => c.type === "tool_result"
  );

// Determine if an assistant message contains tool calls either via the
// toolCalls array or inline content items of type "tool_use".
const hasAssistantToolCalls = (m: Message): boolean => {
  if (m.role !== "assistant") return false;

  const toolCallsCount = Array.isArray(m.toolCalls) ? m.toolCalls.length : 0;

  const contentHasToolUse = Array.isArray(m.content)
    ? (m.content as Array<{ type?: string }>).some(
        (c) => !!c && c.type === "tool_use"
      )
    : false;

  return toolCallsCount > 0 || contentHasToolUse;
};

type AssistantChainPosition = "none" | "single" | "start" | "middle" | "end";

// Computes where an assistant message sits within an assistant chain, ignoring
// user messages that only contain tool results. Real user messages break chains.
const getAssistantChainPosition = (
  allMessages: Message[],
  currentIndex: number
): AssistantChainPosition => {
  const current = allMessages[currentIndex];
  if (!current || current.role !== "assistant") return "none";

  const isAssistantStreamingEmpty = (m: Message): boolean =>
    m.role === "assistant" &&
    m.displayState?.kind === "displaying" &&
    ((m.displayState as { visibleText?: string }).visibleText?.length ?? 0) ===
      0;

  const findNeighborAssistant = (direction: -1 | 1): boolean => {
    let i = currentIndex + direction;
    while (i >= 0 && i < allMessages.length) {
      const candidate = allMessages[i];
      // Skip hidden/system-like messages that shouldn't break chains
      if (
        candidate.role === "tool" ||
        isToolResultOnlyUser(candidate) ||
        isAssistantStreamingEmpty(candidate)
      ) {
        i += direction;
        continue;
      }
      return candidate.role === "assistant";
    }
    return false;
  };

  const hasPrevAssistant = findNeighborAssistant(-1);
  const hasNextAssistant = findNeighborAssistant(1);

  if (!hasPrevAssistant && !hasNextAssistant) return "single";
  if (!hasPrevAssistant && hasNextAssistant) return "start";
  if (hasPrevAssistant && hasNextAssistant) return "middle";
  return "end";
};

// Inline memoized message row to minimize re-renders for unchanged rows
type MessageRowProps = {
  message: Message;
  role: Message["role"];
  chainPosition: AssistantChainPosition;
  disableAnimations: boolean;
  toolStatusRenderer?: ChatMessageProps["toolStatusRenderer"];
};

const InlineChatMessage = ({
  message,
  disableAnimations,
  toolStatusRenderer,
}: Pick<
  MessageRowProps,
  "message" | "disableAnimations" | "toolStatusRenderer"
>) => {
  const { persona } = useAdonisChat();
  switch (message.role) {
    case "user":
      return (
        <UserMessage message={message} disableAnimations={disableAnimations} />
      );
    case "assistant":
      return (
        <AssistantMessage
          message={message}
          disableAnimations={disableAnimations}
          persona={persona}
          toolStatusRenderer={toolStatusRenderer}
        />
      );
    default:
      return (
        <div className="mb-2 text-center text-sm text-gray-500">
          Unknown message type: {message.role}
        </div>
      );
  }
};

const MemoMessageRowInner = ({
  message,
  role,
  chainPosition,
  disableAnimations,
  toolStatusRenderer,
}: MessageRowProps) => {
  const chainClasses =
    chainPosition !== "none"
      ? ["assistant-chain", `assistant-chain--${chainPosition}`]
      : [];
  const wrapperClassName = ["message", ...chainClasses].join(" ");

  return (
    <div
      className={wrapperClassName}
      data-role={role}
      data-tool-result-only={isToolResultOnlyUser(message)}
      data-has-tool-calls={hasAssistantToolCalls(message)}
      data-assistant-chain={chainPosition}
    >
      <InlineChatMessage
        message={message}
        disableAnimations={disableAnimations}
        toolStatusRenderer={toolStatusRenderer}
      />
    </div>
  );
};

const MemoMessageRow = memo(MemoMessageRowInner);
MemoMessageRow.displayName = "MemoMessageRow";

/**
 * MessageList component that handles CSS-based message grouping.
 * Assistant messages with tool calls between them get visually merged.
 * User messages break the visual grouping.
 */
export const MessageList = ({
  messages,
  disableAnimations = false,
  toolStatusRenderer,
}: MessageListProps) => {
  return (
    <div className="conversation">
      {messages.map((message, idx) => {
        if (message.role === "tool") return null;
        // Skip rendering assistant wrapper rows that are streaming with no visible text yet
        if (
          message.role === "assistant" &&
          message.displayState?.kind === "displaying" &&
          ((message.displayState as { visibleText?: string }).visibleText
            ?.length ?? 0) === 0
        ) {
          return null;
        }
        const role = message.role;
        const chainPosition = getAssistantChainPosition(messages, idx);
        return (
          <MemoMessageRow
            key={message.messageId ?? `${message.role}-${idx}`}
            message={message}
            role={role}
            chainPosition={chainPosition}
            disableAnimations={disableAnimations}
            toolStatusRenderer={toolStatusRenderer}
          />
        );
      })}
    </div>
  );
};
